import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';

import { featureFlagConfig } from 'hooks/useFeatureFlag';
import { AuthCookieService } from 'services/cookie/authCookieService';
import { getOrCreateViewingSessionId } from 'utils/anonymousSession';
import { Endpoints } from 'utils/endpoints';

// Requires the specific requested `flag` key to be present and a real boolean — a response
// shaped differently (e.g. `{}`, or `{ [flag]: "false" }`) would otherwise be accepted as-is,
// either leaving the flag permanently unresolved (never releasing PreviewByContentId's loading
// gate) or letting a truthy non-boolean string flip the remote/local branch via JS coercion.
function isFlagResponse(value: unknown, flag: string): value is Record<string, boolean> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return typeof (value as Record<string, unknown>)[flag] === 'boolean';
}

const ANONYMOUS_FLAG_TIMEOUT_MS = 5000;

// TODO(SP-2757): the anonymous-context endpoint this queries is not yet implemented by the core
// API (api.proposify.com, not part of this workspace) — requests will fail until that ships.
// Uses a plain `fetch` (not `apiClient`) so a future non-2xx response can never trigger
// apiClient's shared 401 interceptor (token refresh / logout redirect), which would otherwise
// hard-navigate an anonymous preview viewer away from the page. Always resolves to `false` on
// any failure so callers (RemoteMicrofrontendBridge / the page's own flag branch) fall back to
// local rendering instead of throwing. Aborts after ANONYMOUS_FLAG_TIMEOUT_MS so a hung request
// (rather than a promptly-failed one) can't leave the flag unresolved forever — see
// RemoteMicrofrontendBridge.tsx's probeRemoteEntry for the same pattern.
const anonymousFeatureFlagConfig = (sessionId: string, flag: string) =>
  ({
    queryKey: [flag, 'anonymous', sessionId],
    queryFn: async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), ANONYMOUS_FLAG_TIMEOUT_MS);
      try {
        const url = `${globalThis.env.VITE_API_ENDPOINT}/${Endpoints.getAnonymousFeatureFlagContext(sessionId, flag)}`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return { [flag]: false };
        const data: unknown = await response.json();
        return isFlagResponse(data, flag) ? data : { [flag]: false };
      } catch {
        return { [flag]: false };
      } finally {
        clearTimeout(timeout);
      }
    },
    staleTime: Infinity,
  } satisfies UseQueryOptions);

export interface FeatureFlagAnonymousStatus {
  flags: Record<string, boolean | undefined>;
  isError: boolean;
  isLoading: Record<string, boolean>;
}

// Anonymous-safe counterpart to `useFeatureFlagStatus` (`hooks/useFeatureFlag.ts`) — that hook
// unconditionally calls `useCurrentUser()`, which throws with no `UserProvider` ancestor. This
// hook never touches `UserProvider`/React context; it branches purely on cookie state, so it's
// safe to use on routes like `/document/:contentId` that render for anonymous viewers.
//
// Returns the same `{ flags, isError }` shape as `useFeatureFlagStatus` so callers can gate on
// the flag being genuinely resolved (`flags[key] !== undefined`) rather than treating "still
// loading" the same as "resolved false" — the latter causes a page to mount its local render
// (and every fetch that comes with it) immediately, then remount everything a second time via
// the remote bridge once the flag resolves to true a moment later.
export const useFeatureFlagAnonymous = (flags: string[]): FeatureFlagAnonymousStatus => {
  // AuthCookieService.getLegacyUserId() already self-guards and returns 0 for "no valid id"
  // (it reads FrontendUserCookieService, the always-non-httpOnly frontend_user_token cookie) —
  // an extra isAccessTokenSet() gate here is unnecessary and would just duplicate this check.
  const legacyUserId = AuthCookieService.getLegacyUserId();
  const sessionId = useMemo(() => (legacyUserId ? null : getOrCreateViewingSessionId()), [legacyUserId]);

  const queryResults = useQueries({
    queries: flags.map((flag) =>
      legacyUserId
        ? { ...featureFlagConfig(legacyUserId, flag), enabled: true }
        : { ...anonymousFeatureFlagConfig(sessionId ?? '', flag), enabled: !!sessionId }
    ),
  });

  return useMemo(() => {
    let result: Record<string, boolean> = {};
    let isError = false;
    const isLoading: Record<string, boolean> = {};
    queryResults.forEach(({ data, isError: queryIsError, isLoading: queryIsLoading }, index) => {
      if (data) result = { ...result, ...data };
      if (queryIsError) isError = true;
      isLoading[flags[index]] = queryIsLoading;
    });
    return { flags: result, isError, isLoading };
  }, [queryResults, flags]);
};
